import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, Message } from '@/lib/supabase';
// emailService eliminado
import { Loader2, Mail, ArrowRight, MessageSquare, Phone, User, Clock, Send, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { timeAgo } from '@/lib/utils';

export function MessagesPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replySuccess, setReplySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchMessages();
    }
  }, [user]);

  async function fetchMessages() {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*, listings:listings!messages_storage_id_fkey(title)')
        .eq('owner_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleSendReply = async (messageId: string) => {
    if (!replyText.trim()) return;
    
    setSendingReply(true);
    try {
      const { error } = await supabase
        .from('message_replies')
        .insert({
          message_id: messageId,
          reply_content: replyText
        });

      if (error) throw error;

      // La base de datos envía el email automáticamente al detectar la respuesta
      setReplySuccess(messageId);
      setReplyText('');
      setTimeout(() => {
        setReplySuccess(null);
        setReplyingTo(null);
      }, 5000);

    } catch (error) {
      console.error('Error enviando respuesta:', error);
      alert('Hubo un error al enviar la respuesta. Inténtalo de nuevo.');
    } finally {
      setSendingReply(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-slate-600 mb-4">Inicia sesión para ver tus mensajes</p>
        <Link to="/auth" className="text-blue-600 font-bold hover:underline">Ir al Login</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-7 h-7 text-blue-600" />
                Buzón de Mensajes
            </h1>
            <p className="text-slate-500 text-sm mt-1">Gestiona los contactos de tus anuncios</p>
        </div>
        <Link to="/profile" className="text-sm font-medium text-slate-600 hover:text-blue-600 bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm transition-colors">
            Volver al perfil
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : messages.length > 0 ? (
        <div className="space-y-4">
          {messages.map(msg => (
            <div key={msg.id} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-all duration-200 group">
              <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="bg-blue-100 p-1.5 rounded-full">
                            <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <h3 className="font-bold text-slate-900 text-lg">{msg.sender_name}</h3>
                        <span className="text-xs text-slate-400 font-normal flex items-center gap-1 ml-2">
                            <Clock className="w-3 h-3" /> {timeAgo(msg.created_at)}
                        </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-2">
                        <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                            <Mail className="w-3.5 h-3.5 text-slate-400" />
                            <a href={`mailto:${msg.sender_email}`} className="hover:text-blue-600 font-medium">{msg.sender_email}</a>
                        </div>
                        {msg.sender_phone && (
                            <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                <a href={`tel:${msg.sender_phone}`} className="hover:text-blue-600 font-medium">{msg.sender_phone}</a>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="text-right">
                    <div className={`text-xs font-bold px-3 py-1.5 rounded-full inline-block ${msg.listings ? 'text-blue-700 bg-blue-50 border border-blue-100' : 'text-red-600 bg-red-50 border border-red-100'}`}>
                        {msg.listings ? `Ref: ${msg.listings.title}` : 'Anuncio eliminado'}
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                        {new Date(msg.created_at).toLocaleDateString()}
                    </div>
                </div>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-xl text-slate-700 text-sm leading-relaxed border border-slate-100 relative">
                <div className="absolute top-0 left-4 -translate-y-1/2 w-3 h-3 bg-slate-50 border-t border-l border-slate-100 rotate-45"></div>
                "{msg.message}"
              </div>

              <div className="mt-4 pt-4 border-t border-slate-50">
                {replySuccess === msg.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg animate-in fade-in">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Respuesta enviada correctamente</span>
                    </div>
                  </div>
                ) : replyingTo === msg.id ? (
                  <div className="animate-in slide-in-from-top-2 duration-200">
                    <textarea
                      autoFocus
                      placeholder="Escribe tu respuesta aquí..."
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm mb-3 resize-none"
                      rows={3}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                    />
                    <div className="flex gap-2 justify-end">
                      <button 
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                        className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors"
                      >
                        Cancelar
                      </button>
                      <button 
                        onClick={() => handleSendReply(msg.id)}
                        disabled={sendingReply || !replyText.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Enviar Respuesta
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end gap-3">
                    <a 
                        href={`mailto:${msg.sender_email}?subject=Re: Interés en ${msg.listings?.title || 'tu trastero'}`}
                        className="inline-flex items-center gap-2 px-4 py-2 text-slate-600 bg-white border border-slate-200 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                    >
                        <Mail className="w-4 h-4" />
                        Email externo
                    </a>
                    <button 
                        onClick={() => setReplyingTo(msg.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200"
                    >
                        Responder <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 border-dashed shadow-sm">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Buzón vacío</h3>
          <p className="text-slate-500 max-w-sm mx-auto">
            Aún no has recibido mensajes. Publica más anuncios o mejora los existentes para atraer interesados.
          </p>
          <div className="mt-8">
            <Link to="/publish" className="text-blue-600 font-bold hover:underline">
                Publicar nuevo anuncio
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
